import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import {
  X_INFORMA_IDENTITY,
  X_INFORMA_USER_ACCESS_TOKEN,
} from '../../../../../constants';
import API from '../../../../../api/LeadGenService/v1';
import { fetchLeadGenFormAction } from '../../../../../store/commonDucks/leadGenForm';
import { useRootSelector } from '../../../../../store/hooks';
import {
  selectLeadGenForm,
  selectLeadGenGateType,
} from '../../../../../store/features/leadGen';
import { selectPageConfig } from '../../../../../store/features/pageConfig';
import { selectSiteContent } from '../../../../../store/features/siteContent';
import { beautifyUrl, redirect } from '../../../../../helpers';
import { isKnect365Tenant } from '../../../../../helpers/knect365TenantHelpers';
import { transformAdHocValues } from './utils/transformAdHocValues';
import { transformAdHocQuestions } from './utils/transformAdHocQuestions';
import { getAdHocDefaultValues } from './utils/getAdHocDefaultValues';
import { addProfilePrefix } from './utils/addProfilePrefix';
import { getMarketingPreferencesValues } from './utils/getMarketingPreferencesValues';
import {
  selectSiteHeader,
  selectSiteHeaderData,
} from '../../../../../store/features/siteHeader';
import { transformLeadGenProfile } from '../../../../../utils/transformLeadGenProfile';
import addPreviewPrefixToUrl from '../../../../../helpers/preview/addPreviewPrefixToUrl';
import gtmService from '../../../../../utils/gtm';
import { selectOptions } from '../../../../../store/features/options';
import { getAuthorizationParamsFromCookies } from '../../../../../helpers/authorization/getAuthorizationParamsFromCookies';
import Layout from './Layout';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import UpdateForm from './UpdateForm';
import QuestionForm from './QuestionForm';
import MarketingPreferencesForm from './MarketingPreferencesForm';
import Paper from '../../../../hub/brandHub/pages/account/shared/Paper';
import PublicPageLayout from '../../../../hub/brandHub/pages/account/shared/PublicPageLayout';

export default function LeadGen() {
  const [cookies] = useCookies(['access_token', 'ping-token', 'tracker_id']);
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [registeredGuestId, setRegisteredGuestId] = useState();
  const { siteId, siteType, sitePath, tenantId, canonicalUrl, location } =
    useRootSelector(selectPageConfig);
  const { pingAuthIdentityEnabled } = useRootSelector(selectOptions);
  const { id: pageId } = useRootSelector(selectSiteContent);
  const gateType = useRootSelector(selectLeadGenGateType);
  const {
    form: {
      type,
      adHocQuestions: adHoc = [],
      profile = [],
      marketingPermissions: { consentForms = [] },
    },
    gateId,
  } = useRootSelector(selectLeadGenForm);
  const {
    data: { nameFormat },
  } = useRootSelector(selectSiteHeader);
  const { title: eventTitle } = useRootSelector(selectSiteHeaderData);

  const adHocQuestions = transformAdHocQuestions(adHoc);
  const profileQuestions = transformLeadGenProfile(
    nameFormat,
    transformAdHocQuestions(profile),
  );

  const handleDownloadingAgenda = (fn, shouldCreateAccount) => {
    const ctaTitle = shouldCreateAccount
      ? 'Download & Create an Account'
      : 'Download Now';
    let filename;
    const url = beautifyUrl(
      `${window.location.origin}/${sitePath}/${i18n.language}/downloads/agenda`,
    );

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }

        const header = response.headers.get('Content-Disposition');
        const parts = header.split(';');
        filename = parts[1].split('=')[1].replaceAll('"', '');

        return response.blob();
      })
      .then((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        gtmService.ga4DownloadClick({
          cta_title: ctaTitle,
          filename,
        });
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        if (typeof fn === 'function') {
          fn();
        }
      });
  };

  const handleRegister = async (values) => {
    if (values.company) return;

    setErrorMessage('');

    const marketingPreferences = getMarketingPreferencesValues(
      values,
      consentForms,
      canonicalUrl,
    );
    const adHocValues = transformAdHocValues('adHocAnswers', values, [
      ...adHocQuestions,
      ...addProfilePrefix(profileQuestions),
    ]);
    const profileValues = {
      ...adHocValues.profile,
      ...values.profile,
    };

    const payload = {
      marketingPreferences,
      ...adHocValues,
      ...profileValues,
    };
    delete payload.profile;

    const { host, pathname } = document.location;
    const queryParams = {
      gate_id: gateId,
      login_url: `https://${host}${pathname}`,
    };

    if (cookies.tracker_id) {
      queryParams.priority_code = cookies.tracker_id;
    }

    const params = qs.stringify(queryParams, {
      addQueryPrefix: true,
    });
    let endpoint = API.createLeadGenUser({ queryParams: params });

    if (!values.shouldCreateAccount) {
      endpoint = API.createLeadGenGuest({ queryParams: params });
      payload.registerUrl = `https://${host}/account/register/`;
      delete payload.password;
    }

    const knect365Tenant = isKnect365Tenant(tenantId);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        Object.assign(payload, {
          userAttributes: [
            {
              name: 'custom:custom_email',
              value: knect365Tenant ? 'default' : tenantId.toLowerCase(),
            },
          ],
        }),
      ),
    });

    if (response.ok) {
      const { userId } = await response.json();
      setErrors({});
      setErrorMessage();

      if (values.shouldCreateAccount) {
        gtmService.ga4UserSignUp(
          'User signup',
          'agenda_signup_form_id',
          eventTitle,
        );
      }

      return handleDownloadingAgenda(() => {
        if (values.shouldCreateAccount) {
          const u = btoa(payload.email);
          const origin = document.location.origin;
          const url = addPreviewPrefixToUrl({
            url: `/account/register/success/?username=${u}`,
            pathname: location?.pathname,
            hostname: location?.hostname,
          });

          gtmService.ga4GenerateLead({
            event_label: `User register to access agenda on event: ${eventTitle}`,
            form_id: 'leadgen_download_form_id',
            form_title: eventTitle,
          });

          redirect(`${origin}${url}`);
        } else {
          setRegisteredGuestId(userId);
        }
      }, values.shouldCreateAccount);
    }

    const body = await response.json();
    setErrors(body.errors);
    setErrorMessage(body.message);
  };

  const handleUpdate = async (values) => {
    const marketingPreferences = getMarketingPreferencesValues(
      values,
      consentForms,
      canonicalUrl,
    );
    const adHocValues = transformAdHocValues('adHocAnswers', values, [
      ...adHocQuestions,
      ...profileQuestions,
    ]);

    const payload = { marketingPreferences, ...adHocValues };

    if (Object.keys(payload.profile).length === 0) {
      delete payload.profile;
    }

    const { token, identity, ...authorizationParams } =
      getAuthorizationParamsFromCookies({
        siteType,
        pingAuthIdentityEnabled,
        getTokenFormCookie: (tokenCookieName) => cookies[tokenCookieName],
      });

    const queryParams = {
      ...authorizationParams,
      gate_id: gateId,
    };

    if (cookies.tracker_id) {
      queryParams.priority_code = cookies.tracker_id;
    }

    const response = await fetch(
      API.updateLeadGenUser({
        queryParams: qs.stringify(queryParams, {
          addQueryPrefix: true,
        }),
      }),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { [X_INFORMA_USER_ACCESS_TOKEN]: token }),
          ...(identity && { [X_INFORMA_IDENTITY]: identity }),
        },
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      gtmService.ga4UserLogin(
        `User log in to ${eventTitle}`,
        'agenda_log_in_form_id',
        eventTitle,
      );

      gtmService.ga4GenerateLead({
        event_label: `User register to access agenda on event: ${eventTitle}`,
        form_id: 'leadgen_download_form_id',
        form_title: eventTitle,
      });

      document.location.reload();
    }
  };

  const handleCountryChange = useCallback(
    (selectedCountry) => {
      const authorizationParams = getAuthorizationParamsFromCookies({
        siteType,
        pingAuthIdentityEnabled,
        getTokenFormCookie: (tokenCookieName) => cookies[tokenCookieName],
      });

      const queryParams = {
        ...authorizationParams,
        selectedCountry,
      };

      const params = {
        leadGenType: gateType,
        leadGenId: gateType === 'landing-page' ? pageId : siteId,
        queryParams,
      };
      dispatch(fetchLeadGenFormAction(params));
    },
    [
      dispatch,
      gateType,
      pageId,
      siteId,
      siteType,
      pingAuthIdentityEnabled,
      cookies,
    ],
  );

  const adHocQuestionForm = (
    <QuestionForm
      title="Tell us more about yourself"
      text="<p>This will help ensure you see articles, market intelligence and event updates relevant to you.</p>"
      questions={adHocQuestions}
    />
  );

  const marketingPreferencesForm = (
    <MarketingPreferencesForm consentForms={consentForms} />
  );

  const adHocDefaultValues = getAdHocDefaultValues('adHocAnswers', [
    ...adHocQuestions,
    ...profileQuestions,
  ]);

  if (registeredGuestId) {
    const registerURL = `/account/register/?u=${registeredGuestId}`;

    return (
      <PublicPageLayout>
        <div className="container">
          <div className="row">
            <div className="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
              <Paper
                heading="Thank you for downloading!"
                text={
                  <p>
                    Save time next time by creating an account now. Click{' '}
                    <a href={registerURL}>here</a> to get started.
                  </p>
                }
              />
            </div>
          </div>
        </div>
      </PublicPageLayout>
    );
  }

  return (
    <Layout>
      {type === 'REGISTRATION' && (
        <>
          <RegisterForm
            onSubmit={handleRegister}
            onCountryChange={handleCountryChange}
            adHocQuestionForm={adHocQuestionForm}
            marketingPreferencesForm={marketingPreferencesForm}
            errorMessage={errorMessage}
            errors={errors}
          >
            <QuestionForm questions={addProfilePrefix(profileQuestions)} />
          </RegisterForm>
          <LoginForm />
        </>
      )}
      {type === 'UPDATE' && (
        <UpdateForm
          onSubmit={handleUpdate}
          onCountryChange={handleCountryChange}
          defaultValues={adHocDefaultValues}
        >
          <QuestionForm
            title="Access to this content requires additional details"
            questions={profileQuestions}
          />
          {adHocQuestionForm}
          {marketingPreferencesForm}
        </UpdateForm>
      )}
    </Layout>
  );
}
